import { z } from 'zod'

/** Validates data coming FROM Supabase (snake_case, ISO strings) */
export const CloudTaskSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  completed: z.boolean(),
  life_space: z.enum(['personal', 'work', 'home']).nullable().optional(),
  project_id: z.string().nullable().optional(),
  section_id: z.string().nullable().optional(),
  order: z.number().nullable().optional().default(0),
  tags: z.string().array().default([]),
  attention: z.string().nullable().optional(),
  estimated_duration: z.number().nullable().optional(),
  duration_mode: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  start_period: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  due_time: z.string().nullable().optional(),
  due_period: z.string().nullable().optional(),
  paused_at: z.string().nullable().optional(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates data going TO Supabase (subset for create/update) */
export const CloudTaskPayloadSchema = z.object({
  title: z.string().min(1).trim(),
  description: z.string().nullable().optional(),
  completed: z.boolean(),
  life_space: z.enum(['personal', 'work', 'home']),
  project_id: z.string().nullable().optional(),
  section_id: z.string().nullable().optional(),
  order: z.number(),
  tags: z.string().array().optional(),
  attention: z.string().nullable().optional(),
  estimated_duration: z.number().nullable().optional(),
  duration_mode: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  start_period: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  due_time: z.string().nullable().optional(),
  due_period: z.string().nullable().optional(),
  paused_at: z.string().nullable().optional(),
  local_id: z.string().optional(),
})

export type ValidatedCloudTask = z.infer<typeof CloudTaskSchema>
export type ValidatedCloudTaskPayload = z.infer<typeof CloudTaskPayloadSchema>

/** Validates project data coming FROM Supabase */
export const CloudProjectSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().optional(), // present in DB but not used during sync
  status: z.enum(['active', 'completed', 'archived']),
  life_space: z.enum(['personal', 'work', 'home']),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  progress: z.number(),
  order: z.number(),
  local_id: z.string().nullable().optional(),
  is_system: z.boolean().nullable().optional(),
  system_kind: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates project data going TO Supabase */
export const CloudProjectPayloadSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.enum(['active', 'completed', 'archived']),
  life_space: z.enum(['personal', 'work', 'home']),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  progress: z.number(),
  order: z.number(),
  local_id: z.string().optional(),
  is_system: z.boolean().optional(),
  system_kind: z.string().nullable().optional(),
})

export type ValidatedCloudProject = z.infer<typeof CloudProjectSchema>
export type ValidatedCloudProjectPayload = z.infer<typeof CloudProjectPayloadSchema>

/** Validates section data coming FROM Supabase */
export const CloudSectionSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  project_id: z.string(),
  title: z.string(),
  order: z.number(),
  collapsed: z.boolean(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates section data going TO Supabase */
export const CloudSectionPayloadSchema = z.object({
  project_id: z.string(),
  title: z.string(),
  order: z.number(),
  collapsed: z.boolean(),
  local_id: z.string().optional(),
})

export type ValidatedCloudSection = z.infer<typeof CloudSectionSchema>
export type ValidatedCloudSectionPayload = z.infer<typeof CloudSectionPayloadSchema>

/** Validates tag data coming FROM Supabase */
export const CloudTagSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  color: z.string(),
  description: z.string().nullable().optional(),
  usage_count: z.number().int().default(0),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates tag data going TO Supabase */
export const CloudTagPayloadSchema = z.object({
  name: z.string(),
  color: z.string(),
  description: z.string().nullable().optional(),
  usage_count: z.number().int().optional(),
  local_id: z.string().optional(),
})

export type ValidatedCloudTag = z.infer<typeof CloudTagSchema>
export type ValidatedCloudTagPayload = z.infer<typeof CloudTagPayloadSchema>

/** Validates goal data coming FROM Supabase */
export const CloudGoalSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  life_space: z.enum(['personal', 'work', 'home']),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates goal data going TO Supabase */
export const CloudGoalPayloadSchema = z.object({
  title: z.string().trim(),
  description: z.string().nullable().optional(),
  life_space: z.enum(['personal', 'work', 'home']),
  local_id: z.string().optional(),
})

export type ValidatedCloudGoal = z.infer<typeof CloudGoalSchema>
export type ValidatedCloudGoalPayload = z.infer<typeof CloudGoalPayloadSchema>
